import type {
    ChamferFeatureDefinition,
    ChamferType,
    FilletFeatureDefinition,
    FilletType,
} from '@puppycad/cad-core';
import {
    convertMmToM,
    hasAuthoritativeBlendSelectionRuntimeHint,
    isSupportedFaceFilletMode,
    normalizeChamferDefinitionForFaceFaceMode as normalizeChamferDefinitionForFaceFaceModeCore,
    normalizeChamferDefinitionForTypeSwitch as normalizeChamferDefinitionForTypeSwitchCore,
    normalizeFilletDefinitionForFaceFilletMode as normalizeFilletDefinitionForFaceFilletModeCore,
    validateChamferDraftSubmission,
} from '@puppycad/cad-core';
import type { BlendSelectionDisplayItem } from './types';
import { resolveFaceSetItemRefs, type FilletFaceSetId } from './stage-two';
import {
    DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT,
    ensureVariableRadiusEndpointPoints,
} from './variable-radius';

const CURRENT_EDITABLE_FILLET_TYPES = new Set<FilletType>(['constant', 'variable', 'face']);
const CURRENT_EDITABLE_CHAMFER_TYPES = new Set<ChamferType>(['angle-distance', 'distance-distance', 'vertex', 'offset-face', 'face-face']);

function isPositiveNumber(value: number | undefined): boolean {
    return typeof value === 'number'
        && Number.isFinite(value)
        && value > 0;
}

function hasAuthoritativeRuntimeHint(
    item: BlendSelectionDisplayItem,
): boolean {
    return hasAuthoritativeBlendSelectionRuntimeHint(item);
}

export function clonePlainData<T>(value: T): T {
    if (typeof structuredClone === 'function') {
        try {
            return structuredClone(value);
        } catch {
            // 某些测试桩对象不支持 structuredClone，退回 JSON 深拷贝即可。
        }
    }
    return JSON.parse(JSON.stringify(value)) as T;
}

export function isEditableTarget(target: EventTarget | null): boolean {
    const element = target instanceof HTMLElement ? target : null;
    if (!element) {
        return false;
    }
    if (element.isContentEditable) {
        return true;
    }
    const tagName = element.tagName.toLowerCase();
    if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
        return true;
    }
    return Boolean(element.closest('input, textarea, select, [contenteditable="true"]'));
}

export function reverseFacePreference(faceRefs: string[] | undefined): string[] | undefined {
    if (!Array.isArray(faceRefs) || faceRefs.length <= 1) {
        return faceRefs ? [...faceRefs] : undefined;
    }
    return [...faceRefs].reverse();
}

/**
 * 现阶段的“编辑已有特征”只对已经跑通完整提交闭环的类型开放。
 * 这样可以避免用户从特征树进入 Face/Full Round 等尚未完整收口的编辑链路。
 */
export function isCurrentlyEditableFilletType(type: FilletType): boolean {
    return CURRENT_EDITABLE_FILLET_TYPES.has(type);
}

export function isCurrentlyEditableChamferType(type: ChamferType): boolean {
    return CURRENT_EDITABLE_CHAMFER_TYPES.has(type);
}

function hasRequiredFilletFaceSets(
    definition: FilletFeatureDefinition,
    requiredSetIds: FilletFaceSetId[],
): boolean {
    return requiredSetIds.every((setId) => resolveFaceSetItemRefs(definition, setId).length > 0);
}

function isFaceFilletInCurrentSupportedSubset(
    definition: FilletFeatureDefinition,
): boolean {
    /**
     * 当前 cad-core 已接通 Face Fillet 的对称、弦宽和相邻面组非对称模式以及 Help Point 过滤。
     * 非对称模式需要双半径；具体是否存在共享边由 solver 层负责给出几何错误。
     * constantWidth 仍未接通。
     */
    const mode = definition.faceFilletMode ?? 'symmetric';
    if (!isSupportedFaceFilletMode(mode) || definition.constantWidth === true) {
        return false;
    }
    if (mode === 'chord-width') {
        return isPositiveNumber(definition.chordWidth);
    }
    if (mode === 'asymmetric') {
        return isPositiveNumber(definition.radius) && isPositiveNumber(definition.radius2);
    }
    return isPositiveNumber(definition.radius);
}

/**
 * 规范化倒角定义在类型切换后的残留字段。
 *
 * @param definition 原始倒角定义。
 * @param nextType 目标倒角类型。
 * @returns 与目标类型一致的倒角定义。
 */
export function normalizeChamferDefinitionForTypeSwitch(
    definition: ChamferFeatureDefinition,
    nextType: ChamferType,
): ChamferFeatureDefinition {
    return normalizeChamferDefinitionForTypeSwitchCore({
        definition,
        nextType,
    });
}

export function normalizeChamferDefinitionForFaceFaceMode(
    definition: ChamferFeatureDefinition,
    mode: NonNullable<ChamferFeatureDefinition['faceFaceMode']>,
): ChamferFeatureDefinition {
    return normalizeChamferDefinitionForFaceFaceModeCore(definition, mode);
}

export function normalizeFilletDefinitionForTypeSwitch(
    definition: FilletFeatureDefinition,
    nextType: FilletType,
): FilletFeatureDefinition {
    /**
     * 圆角类型切换时，必须主动清理跨类型遗留字段。
     * 否则像 radius2 / partialEdges / helpPoint 这类旧值会继续挂在定义上，
     * 让 PropertyManager、preview 与 cad-core 对“当前类型是否可提交”出现分叉。
     */
    const nextDefinition: FilletFeatureDefinition = {
        ...definition,
        type: nextType,
    };

    if (nextType !== 'variable') {
        nextDefinition.variablePoints = [];
        nextDefinition.variableInstanceCount = undefined;
        nextDefinition.variableMode = undefined;
        nextDefinition.variableProfile = undefined;
        nextDefinition.variableTransition = undefined;
    } else {
        const instanceCount = nextDefinition.variableInstanceCount ?? DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT;
        const edgeItemRefs = (nextDefinition.items ?? [])
            .filter((item) => item.kind === 'edge')
            .map((item) => item.persistentRef);
        nextDefinition.variableInstanceCount = instanceCount;
        nextDefinition.variableMode = nextDefinition.variableMode ?? 'symmetric';
        nextDefinition.variableProfile = nextDefinition.variableMode === 'asymmetric' ? 'elliptic' : 'circular';
        nextDefinition.variableTransition = nextDefinition.variableTransition ?? 'smooth';
        nextDefinition.variablePoints = ensureVariableRadiusEndpointPoints(
            nextDefinition.variablePoints,
            edgeItemRefs,
            instanceCount,
            nextDefinition.radius ?? convertMmToM(10),
            nextDefinition.radiusExpression ?? null,
        );
    }

    if (nextType !== 'constant') {
        nextDefinition.edgeFilletMode = 'symmetric';
        nextDefinition.asymmetricRadiiFlipped = false;
        nextDefinition.partialEdgeParametersEnabled = false;
        nextDefinition.partialEdges = [];
        nextDefinition.radius2 = undefined;
        nextDefinition.radius2Expression = null;
    }

    if (nextType !== 'face') {
        nextDefinition.faceFilletMode = 'symmetric';
        nextDefinition.helpPoint = null;
        nextDefinition.trimSurfaces = false;
        nextDefinition.constantWidth = false;
        nextDefinition.chordWidth = undefined;
        nextDefinition.chordWidthExpression = null;
        nextDefinition.faceFilletRadiiFlipped = false;
    } else {
        return normalizeFilletDefinitionForFaceFilletModeCore(
            nextDefinition,
            nextDefinition.faceFilletMode ?? 'symmetric',
        );
    }

    return nextDefinition;
}

function hasBlendPreviewSelectionIdentity(item: BlendSelectionDisplayItem): boolean {
    return typeof item.bodyObjectId === 'string'
        && item.bodyObjectId.length > 0
        && typeof item.persistentRef === 'string'
        && item.persistentRef.length > 0;
}

/**
 * 判断圆角是否具备触发 Worker 预览的最小条件。
 *
 * 设计说明：
 * 1. 预览应优先尝试 Worker 求解，不应被完整提交门禁提前拦截；
 * 2. 只要上游实体与选择引用齐备，就允许进入 preview，让 Worker 返回真实失败原因；
 * 3. 最终是否可提交仍由 `canSubmitFillet` 决定。
 */
export function canPreviewFillet(
    definition: FilletFeatureDefinition,
    upstreamBodyObjectId: string | null,
    items: BlendSelectionDisplayItem[],
): boolean {
    if (!upstreamBodyObjectId || items.length === 0) {
        return false;
    }

    switch (definition.type) {
        case 'constant':
            return items.every(hasBlendPreviewSelectionIdentity)
                && isPositiveNumber(definition.radius);
        case 'variable':
            return items.every((item) => item.kind === 'edge' && hasBlendPreviewSelectionIdentity(item))
                && (definition.variablePoints ?? []).length > 0;
        case 'face':
            return hasRequiredFilletFaceSets(definition, ['faceSet1', 'faceSet2']);
        default:
            return false;
    }
}

export function canSubmitFillet(
    definition: FilletFeatureDefinition,
    upstreamBodyObjectId: string | null,
    items: BlendSelectionDisplayItem[],
): boolean {
    if (!upstreamBodyObjectId) {
        return false;
    }

    switch (definition.type) {
        case 'constant':
            /**
             * 固定大小圆角当前只允许“面参与时=对称、纯边时才可非对称”这一条提交语义。
             * 这里必须和属性栏门禁、视口过滤保持一致，防止 UI 已锁回对称而提交仍带着旧的 asymmetric 漂移。
             */
            return items.length > 0
                && items.every(hasAuthoritativeRuntimeHint)
                && isPositiveNumber(definition.radius)
                && (
                    definition.edgeFilletMode !== 'asymmetric'
                        ? true
                        : !items.some((item) => item.kind === 'face')
                )
                && (
                    definition.edgeFilletMode !== 'asymmetric'
                    || isPositiveNumber(definition.radius2)
                );
        case 'variable':
            return items.length > 0
                && items.every(hasAuthoritativeRuntimeHint)
                && (definition.variablePoints ?? []).length > 0
                && items.every((item) => (
                    item.kind === 'edge'
                    && hasRequiredVariableEndpointRadii(definition, item.persistentRef)
                ))
                && (definition.variablePoints ?? []).every((point) => (
                    (point.role === 'instance' && (point.radius === null || point.radius === undefined))
                    || (
                        isPositiveNumber(point.radius ?? undefined)
                        && (
                            definition.variableMode !== 'asymmetric'
                            || isPositiveNumber(point.radius2 ?? undefined)
                        )
                    )
                ));
        case 'face':
            return hasRequiredFilletFaceSets(definition, ['faceSet1', 'faceSet2'])
                && isFaceFilletInCurrentSupportedSubset(definition);
        default:
            return false;
    }
}

function hasRequiredVariableEndpointRadii(
    definition: FilletFeatureDefinition,
    itemRef: string,
): boolean {
    const endpoints = (definition.variablePoints ?? [])
        .filter((point) => point.itemRef === itemRef && point.role === 'vertex');
    return endpoints.length >= 2
        && endpoints.every((point) => (
            isPositiveNumber(point.radius ?? undefined)
            && (
                definition.variableMode !== 'asymmetric'
                || isPositiveNumber(point.radius2 ?? undefined)
            )
        ));
}

export function canSubmitChamfer(
    definition: ChamferFeatureDefinition,
    upstreamBodyObjectId: string | null,
    items: BlendSelectionDisplayItem[],
): boolean {
    return validateChamferDraftSubmission({
        definition,
        upstreamBodyObjectId,
        items,
    });
}

/**
 * 判断倒角是否具备触发 Worker 预览的最小条件。
 */
export function canPreviewChamfer(
    definition: ChamferFeatureDefinition,
    upstreamBodyObjectId: string | null,
    items: BlendSelectionDisplayItem[],
): boolean {
    if (!upstreamBodyObjectId || items.length === 0) {
        return false;
    }

    switch (definition.type) {
        case 'angle-distance':
        case 'distance-distance':
        case 'vertex':
        case 'offset-face':
            return items.every(hasBlendPreviewSelectionIdentity);
        case 'face-face':
            return items.length > 0;
        default:
            return false;
    }
}

export function describeBlendSelectionTarget(kind: 'fillet' | 'chamfer', type: FilletType | ChamferType): string {
    if (kind === 'fillet') {
        if (type === 'constant') {
            return '请选择要圆角化的边线或面';
        }
        return type === 'face'
            ? '请选择要圆角化的面组'
            : '请选择要圆角化的边线';
    }
    if (type === 'vertex') {
        return '请选择要倒角化的顶点';
    }
    if (type === 'face-face') {
        return '请选择要倒角化的面组';
    }
    if (type === 'angle-distance' || type === 'distance-distance') {
        return '请选择要倒角化的边线或面';
    }
    return '请选择要倒角化的边线或面';
}
